// Update letter when user types
  const handleLetterChange = (e) => {
    const newContent = e.target.value;
    setIntakeLetter(newContent);
    
    // Update cache in real-time
    if (selectedClientId) {
      setClientLetterCache(prevCache => ({
        ...prevCache,
        [selectedClientId]: newContent
      }));
    }
  };import React, { useState, useEffect } from 'react';
import API from '../services/api'; // Adjust import path as needed

function ImprovedClientProfilePage() {
  // Client state management
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    clientName: '',
    phone: '',
    email: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    isMinor: false,
    guardianName: '',
    guardianPhone: '',
    diagnosisCode: '',
    dob: '',
    gender: '',
    reasonForVisit: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    billingEmail: '',
    billingAddress: '',
    notes: ''
  });

  // Intake link modal state
  const [showIntakeLinkModal, setShowIntakeLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Intake letter modal state
  const [showIntakeLetterModal, setShowIntakeLetterModal] = useState(false);
  const [intakeLetter, setIntakeLetter] = useState('');
  const [letterCopied, setLetterCopied] = useState(false);
  const [savedLetters, setSavedLetters] = useState([]);
  
  // Store the current client's letter in a cache to maintain between modal openings
  const [clientLetterCache, setClientLetterCache] = useState({});

  // Fetch all clients belonging to this therapist
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await API.get('/therapist/clients');
      setClients(res.data); // Should be an array of { id, client_name }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  // If user selects an existing client, fetch that client's details
  const handleSelectClient = async (clientId) => {
    setSelectedClientId(clientId);
    if (!clientId) {
      // No client selected - reset form for "new"
      setIsEditingExisting(false);
      resetFormData();
      return;
    }

    try {
      const res = await API.get(`/therapist/clients/${clientId}`);
      if (res.data) {
        setIsEditingExisting(true);
        setFormData({
          clientName: res.data.client_name || '',
          phone: res.data.phone || '',
          email: res.data.email || '',
          emergencyContactName: res.data.emergency_contact_name || '',
          emergencyContactPhone: res.data.emergency_contact_phone || '',
          isMinor: !!res.data.is_minor,
          guardianName: res.data.guardian_name || '',
          guardianPhone: res.data.guardian_phone || '',
          diagnosisCode: res.data.diagnosis_code || '',
          dob: res.data.dob || '',
          gender: res.data.gender || '',
          reasonForVisit: res.data.reason_for_visit || '',
          address: res.data.address || '',
          city: res.data.city || '',
          state: res.data.state || '',
          zipCode: res.data.zip_code || '',
          billingEmail: res.data.billing_email || '',
          billingAddress: res.data.billing_address || '',
          notes: res.data.notes || ''
        });
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
    }
  };

  // Reset form data to default values
  const resetFormData = () => {
    setFormData({
      clientName: '',
      phone: '',
      email: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      isMinor: false,
      guardianName: '',
      guardianPhone: '',
      diagnosisCode: '',
      dob: '',
      gender: '',
      reasonForVisit: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      billingEmail: '',
      billingAddress: '',
      notes: ''
    });
  };

  // Create new client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const response = await API.post('/therapist/clients', {
        clientName: formData.clientName,
        phone: formData.phone,
        email: formData.email,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        isMinor: formData.isMinor,
        guardianName: formData.guardianName,
        guardianPhone: formData.guardianPhone,
        diagnosisCode: formData.diagnosisCode,
        dob: formData.dob,
        gender: formData.gender,
        reasonForVisit: formData.reasonForVisit,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        billingEmail: formData.billingEmail,
        billingAddress: formData.billingAddress,
        notes: formData.notes
      });
      
      alert('Client created successfully');
      
      // Store the newly created client ID
      const newClientId = response.data.id;

      // Offer to generate an intake link for the new client
      if (window.confirm('Would you like to generate an intake form link for this client?')) {
        handleGenerateIntakeLink(newClientId);
      }
      
      // Reset and refresh
      resetFormData();
      setSelectedClientId('');
      setIsEditingExisting(false);
      fetchClients();
    } catch (err) {
      console.error('Error creating client:', err);
      alert('Error creating client');
    }
  };

  // Update existing client
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      alert('No client selected for update');
      return;
    }
    try {
      await API.put(`/therapist/clients/${selectedClientId}`, {
        clientName: formData.clientName,
        phone: formData.phone,
        email: formData.email,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        isMinor: formData.isMinor,
        guardianName: formData.guardianName,
        guardianPhone: formData.guardianPhone,
        diagnosisCode: formData.diagnosisCode,
        dob: formData.dob,
        gender: formData.gender,
        reasonForVisit: formData.reasonForVisit,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        billingEmail: formData.billingEmail,
        billingAddress: formData.billingAddress,
        notes: formData.notes
      });
      alert('Client updated successfully');
      fetchClients();
    } catch (err) {
      console.error('Error updating client:', err);
      alert('Error updating client');
    }
  };

  // Handle intake link generation
  const handleGenerateIntakeLink = async (clientId = null) => {
    const targetId = clientId || selectedClientId;
    
    if (!targetId) {
      alert('Please select a client first');
      return;
    }
    
    setShowIntakeLinkModal(true);
    setGeneratedLink('');
    setErrorMsg('');
    setLoading(true);
    
    try {
      // Calculate date 7 days from now for auto-expiration
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);
      
      const body = {
        clientId: targetId,
        expiresAt: expirationDate.toISOString()
      };

      const res = await API.post('/forms/intake/generate', body);

      if (res.data.success) {
        setGeneratedLink(res.data.link);
        
        // Use client letter from cache if available, otherwise create default
        if (clientLetterCache[targetId]) {
          setIntakeLetter(clientLetterCache[targetId]);
        } else {
          // Create default intake letter with the client's name
          const defaultIntakeLetter = `Hello ${formData.clientName},

Welcome to Avenues Unlimited Counseling Center! To help us get started, please complete our intake form by clicking the link below:

${res.data.link}

This link will expire in 7 days. The form should take about 10-15 minutes to complete. Your information will be securely stored and will help us provide you with the best care possible.

If you have any questions or need assistance, please don't hesitate to contact us at 502-459-9635.

We look forward to working with you!

Avenues Unlimited Counseling Center`;
          setIntakeLetter(defaultIntakeLetter);
          
          // Save to cache for this client
          setClientLetterCache(prev => ({
            ...prev,
            [targetId]: defaultIntakeLetter
          }));
        }
      } else {
        setErrorMsg(res.data.error || 'Error generating link.');
      }
    } catch (err) {
      console.error('Error generating link:', err);
      setErrorMsg('Server error generating link.');
    } finally {
      setLoading(false);
    }
  };

  // Copy link to clipboard
  const copyLinkToClipboard = () => {
    if (!generatedLink) return;
    
    navigator.clipboard.writeText(generatedLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Copy intake letter to clipboard
  const copyIntakeLetterToClipboard = () => {
    if (!intakeLetter) return;
    
    navigator.clipboard.writeText(intakeLetter)
      .then(() => {
        setLetterCopied(true);
        setTimeout(() => setLetterCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  // Save the current intake letter
  const saveIntakeLetter = () => {
    if (!intakeLetter) return;
    
    // Save to client letter cache
    setClientLetterCache(prev => ({
      ...prev,
      [selectedClientId]: intakeLetter
    }));
    
    const newSavedLetter = {
      id: Date.now(),
      clientName: formData.clientName,
      clientId: selectedClientId,
      content: intakeLetter,
      date: new Date().toISOString()
    };
    
    // Save locally and can also save to API if needed
    setSavedLetters(prev => [...prev, newSavedLetter]);
    
    // Optionally save to backend:
    // API.post('/therapist/client-letters', newSavedLetter)
    //   .then(res => console.log('Letter saved'))
    //   .catch(err => console.error('Error saving letter:', err));
    
    alert('Intake letter saved successfully');
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(client => 
    client.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Client Management</h2>
        <div style={styles.searchBar}>
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      <div style={styles.contentWrapper}>
        {/* LEFT PANEL - Client List */}
        <div style={styles.clientListContainer}>
          <div style={styles.listHeader}>
            <h3 style={styles.listTitle}>Client List</h3>
            <button 
              onClick={() => handleSelectClient('')}
              style={styles.newClientButton}
            >
              + New Client
            </button>
          </div>
          
          <div style={styles.clientList}>
            {filteredClients.length > 0 ? (
              filteredClients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    ...styles.clientItem,
                    backgroundColor: selectedClientId === client.id ? '#e6f7ff' : '#fff',
                    borderLeft: selectedClientId === client.id ? '4px solid #1890ff' : 'none',
                    paddingLeft: selectedClientId === client.id ? '12px' : '16px'
                  }}
                  onClick={() => handleSelectClient(client.id)}
                >
                  <div style={styles.clientName}>{client.client_name}</div>
                </div>
              ))
            ) : (
              <div style={styles.noClients}>
                {searchTerm ? 'No clients match your search' : 'No clients found'}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL - Client Form */}
        <div style={styles.formContainer}>
          <form
            onSubmit={isEditingExisting ? handleUpdateClient : handleCreateClient}
            style={styles.form}
          >
            <div style={styles.formHeader}>
              <h3 style={styles.formTitle}>
                {isEditingExisting ? 'Edit Client' : 'New Client'}
              </h3>
              
              {isEditingExisting && (
                <button
                  type="button"
                  onClick={() => handleGenerateIntakeLink()}
                  style={styles.generateLinkButton}
                >
                  Generate Intake Link
                </button>
              )}
            </div>
            
            <div style={styles.formContent}>
              {/* Form Sections */}
              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Basic Information</h4>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Client Name*</label>
                    <input
                      type="text"
                      value={formData.clientName}
                      onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dob}
                      onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      style={styles.input}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Diagnosis Code</label>
                    <input
                      type="text"
                      value={formData.diagnosisCode}
                      onChange={(e) => setFormData({ ...formData, diagnosisCode: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <label style={styles.label}>Reason for Visit</label>
                    <textarea
                      value={formData.reasonForVisit}
                      onChange={(e) => setFormData({ ...formData, reasonForVisit: e.target.value })}
                      style={styles.textarea}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Contact Information</h4>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <label style={styles.label}>Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={styles.input}
                      placeholder="Street Address"
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label style={styles.label}>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Zip Code</label>
                    <input
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>
                  <span style={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      checked={formData.isMinor}
                      onChange={(e) => setFormData({ ...formData, isMinor: e.target.checked })}
                      id="isMinorCheckbox"
                    />
                    <label htmlFor="isMinorCheckbox" style={styles.checkboxLabel}>Client is a Minor</label>
                  </span>
                </h4>
                
                {formData.isMinor && (
                  <div style={styles.minorSection}>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Guardian Name*</label>
                        <input
                          type="text"
                          value={formData.guardianName}
                          onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                          style={styles.input}
                          required={formData.isMinor}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Guardian Phone</label>
                        <input
                          type="tel"
                          value={formData.guardianPhone}
                          onChange={(e) => setFormData({ ...formData, guardianPhone: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Emergency Contact</h4>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Name*</label>
                    <input
                      type="text"
                      value={formData.emergencyContactName}
                      onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Phone*</label>
                    <input
                      type="tel"
                      value={formData.emergencyContactPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Billing Information</h4>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Billing Email</label>
                    <input
                      type="email"
                      value={formData.billingEmail}
                      onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <label style={styles.label}>Billing Address</label>
                    <textarea
                      value={formData.billingAddress}
                      onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                      style={styles.textarea}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Additional Notes</h4>
                <div style={styles.formRow}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      style={styles.textarea}
                      rows={3}
                      placeholder="Add any additional notes about this client here..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.formActions}>
              <button type="submit" style={styles.submitButton}>
                {isEditingExisting ? 'Update Client' : 'Create Client'}
              </button>
              {isEditingExisting && (
                <button
                  type="button"
                  onClick={() => handleSelectClient('')}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Intake Link Generation Modal */}
      {showIntakeLinkModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Generate Intake Form Link</h3>
              <button 
                style={styles.modalClose}
                onClick={() => setShowIntakeLinkModal(false)}
              >
                ×
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {loading ? (
                <div style={styles.loadingContainer}>
                  <p>Generating link...</p>
                </div>
              ) : errorMsg ? (
                <div style={styles.errorContainer}>
                  <p style={styles.errorMessage}>{errorMsg}</p>
                  <button 
                    style={styles.modalButton}
                    onClick={() => setShowIntakeLinkModal(false)}
                  >
                    Close
                  </button>
                </div>
              ) : generatedLink ? (
                <div>
                  <p>The intake form link has been generated for <strong>{formData.clientName}</strong>:</p>
                  
                  <div style={styles.linkContainer}>
                    <a href={generatedLink} target="_blank" rel="noreferrer">{generatedLink}</a>
                  </div>
                  
                  <p style={styles.linkExpires}>
                    This link will expire in 7 days on {new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString()}.
                  </p>
                  
                  <div style={styles.modalActions}>
                    <button 
                      style={linkCopied ? styles.buttonSuccess : styles.modalButton}
                      onClick={copyLinkToClipboard}
                    >
                      {linkCopied ? 'Copied!' : 'Copy Link'}
                    </button>
                    
                    <button 
                      style={styles.modalButtonPrimary}
                      onClick={() => {
                        setShowIntakeLinkModal(false);
                        setShowIntakeLetterModal(true);
                      }}
                    >
                      Prepare Intake Letter
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>
                    Generate an intake form link for <strong>{formData.clientName}</strong> to complete.
                    The link will automatically expire in 7 days.
                  </p>
                  
                  <div style={styles.modalActions}>
                    <button 
                      style={styles.modalButtonPrimary}
                      onClick={() => handleGenerateIntakeLink()}
                    >
                      Generate Link
                    </button>
                    <button 
                      style={styles.modalButton}
                      onClick={() => setShowIntakeLinkModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Intake Letter Modal */}
      {showIntakeLetterModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal} style={{width: '600px'}}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Client Intake Letter</h3>
              <button 
                style={styles.modalClose}
                onClick={() => setShowIntakeLetterModal(false)}
              >
                ×
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <p>Customize the intake letter for {formData.clientName}:</p>
              
              <div style={{position: 'relative', width: '100%', minHeight: '300px'}}>
                <textarea
                  value={intakeLetter}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setIntakeLetter(newValue);
                    // Update cache in real-time
                    if (selectedClientId) {
                      setClientLetterCache(prevCache => ({
                        ...prevCache,
                        [selectedClientId]: newValue
                      }));
                    }
                  }}
                  style={{
                    ...styles.intakeLetterTextarea,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '100%',
                    zIndex: 2
                  }}
                />
              </div>
              
              <div style={styles.modalActions}>
                <button 
                  style={letterCopied ? styles.buttonSuccess : styles.modalButtonPrimary}
                  onClick={copyIntakeLetterToClipboard}
                >
                  {letterCopied ? 'Copied!' : 'Copy Letter'}
                </button>
                
                <button 
                  style={styles.saveButton}
                  onClick={saveIntakeLetter}
                >
                  Save Letter
                </button>
                
                <button 
                  style={styles.modalButton}
                  onClick={() => setShowIntakeLetterModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  title: {
    fontSize: '24px',
    margin: 0,
    color: '#333'
  },
  searchBar: {
    width: '300px'
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  contentWrapper: {
    display: 'flex',
    gap: '20px'
  },
  
  // Client list styles
  clientListContainer: {
    width: '300px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#f9f9f9',
    overflow: 'hidden'
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f1f1f1'
  },
  listTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 'normal'
  },
  newClientButton: {
    backgroundColor: '#1890ff',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  clientList: {
    maxHeight: '650px',
    overflowY: 'auto'
  },
  clientItem: {
    padding: '12px 16px',
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  clientName: {
    fontSize: '14px'
  },
  noClients: {
    padding: '15px',
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic'
  },
  
  // Form container styles
  formContainer: {
    flex: 1,
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff'
  },
  form: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f9f9f9'
  },
  formTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'normal'
  },
  generateLinkButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  formContent: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  formSection: {
    marginBottom: '25px'
  },
  sectionTitle: {
    fontSize: '16px',
    marginTop: 0,
    marginBottom: '15px',
    paddingBottom: '6px',
    borderBottom: '1px solid #eee'
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  checkboxLabel: {
    marginLeft: '8px',
    cursor: 'pointer'
  },
  minorSection: {
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '4px',
    marginBottom: '15px'
  },
  formRow: {
    display: 'flex',
    gap: '15px',
    marginBottom: '15px'
  },
  formField: {
    flex: 1,
    width: '100%'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    resize: 'vertical'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '15px 20px',
    borderTop: '1px solid #eee',
    backgroundColor: '#f9f9f9',
    gap: '10px'
  },
  submitButton: {
    backgroundColor: '#1890ff',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  cancelButton: {
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #ddd',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  
  // Modal styles
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    width: '500px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f9f9f9'
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '22px',
    cursor: 'pointer',
    color: '#999'
  },
  modalBody: {
    padding: '20px',
    backgroundColor: '#ffffff' /* Explicitly white */
  },
  linkContainer: {
    marginTop: '15px',
    padding: '10px',
    background: '#f5f5f5',
    borderRadius: '4px',
    wordBreak: 'break-all'
  },
  linkExpires: {
    color: '#777',
    fontSize: '14px',
    marginTop: '10px'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  modalButton: {
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  modalButtonPrimary: {
    backgroundColor: '#1890ff',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  buttonSuccess: {
    backgroundColor: '#52c41a',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0'
  },
  errorContainer: {
    textAlign: 'center'
  },
  errorMessage: {
    color: '#f5222d',
    marginBottom: '15px'
  },
  intakeLetterTextarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '1.5',
    resize: 'vertical',
    fontFamily: 'monospace',
    backgroundColor: '#ffffff', /* Explicitly white */
    color: '#000000',  /* Explicitly black text */
    outline: 'none',
    position: 'static',
    overflow: 'auto'
  },
  saveButton: {
    backgroundColor: '#faad14',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  }
};

export default ImprovedClientProfilePage;